// Sprint TENANTS TENANTS.B3 (#2339) — destructive/impactful tenant admin
// actions: suspend/reactivate, tier change, seat limit. Feature flags live
// in a separate component (TenantFeatureFlagsSection.tsx) since they render
// inline in the Details tab, not as footer buttons — see this file's
// sibling for that split.
//
// ── No downgrade-warning banner (deliberate, not an oversight) ─────────────
// The original story asked for a warning when downgrading and current
// member count exceeds "the new tier's default seat limit (Starter: 10,
// Growth: 20)". Recon for #2339 found NO authoritative per-tier default
// seat-limit constant anywhere in the codebase — those two numbers exist
// only in the story prompt's own text, not in any server-side source
// (tier-capabilities.ts's "1-10 seats" is prose in a comment, confirmed by
// TENANTS.A1's own recon). Founder decision: do not invent numbers with no
// backing in code. Filed #2393 for the missing constant; this banner is
// simply omitted until that lands, rather than shipped with fabricated
// values.
//
// ── Every confirmation gate is a REAL disabled-until-valid guard, not a
//    cosmetic one — this is the point of the R1+R2+R3 review protocol for
//    this story. Submit buttons are `disabled` (the actual HTML attribute)
//    until every required condition holds; there is no code path that fires
//    the request while any gate is unsatisfied.

import { useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';

// Mirrors tenantAdminActions.service.ts's TENANT_TIERS exactly — server's
// live 5-value enum (ACADEMIC deliberately excluded there; see that file's
// own comment on why a bare tier-flip can't safely touch academic tenants).
const TIERS: { value: string; label: string }[] = [
  { value: 'INDIVIDUAL',       label: 'Individual' },
  { value: 'STARTER',          label: 'Starter' },
  { value: 'GROWTH',           label: 'Growth' },
  { value: 'ENTERPRISE',       label: 'Enterprise' },
  { value: 'PLATFORM_PARTNER', label: 'Platform Partner' },
];

const MIN_REASON_LENGTH = 10;
function isValidReason(reason: string): boolean {
  return reason.trim().length >= MIN_REASON_LENGTH;
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as ApiError | null;
  return (e?.body?.error as string | undefined) ?? fallback;
}

// ── Shared modal shell, matching TokenReimportModal.tsx's established pattern ──

function ModalShell({ title, testId, onClose, children }: { title: string; testId: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', padding: '48px 16px', overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, background: 'var(--bg)',
          borderRadius: 'var(--radius-md, 8px)', border: '1px solid var(--border)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)', padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', fontSize: 18, lineHeight: 1, color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const fieldLabelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, marginTop: 12 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 10px', fontSize: 13, fontFamily: 'var(--font-body)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' };
const errorStyle: React.CSSProperties = { fontSize: 12, color: 'var(--error)', marginTop: 8 };
const footerRowStyle: React.CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 };

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', cursor: 'pointer' }}>
      Cancel
    </button>
  );
}

function SubmitButton({ label, busyLabel, busy, disabled, onClick, testId }: { label: string; busyLabel: string; busy: boolean; disabled: boolean; onClick: () => void; testId: string }) {
  const inactive = busy || disabled;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={inactive}
      data-testid={testId}
      style={{
        fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '6px 14px',
        color: 'white', border: 'none', borderRadius: 6,
        background: inactive ? 'var(--text-muted)' : 'var(--error)',
        cursor: inactive ? 'not-allowed' : 'pointer',
      }}
    >
      {busy ? busyLabel : label}
    </button>
  );
}

const smallBtnStyle: React.CSSProperties = { padding: '5px 12px', fontSize: 13, fontFamily: 'var(--font-body)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' };

// ── Suspend ──────────────────────────────────────────────────────────────

function SuspendModal({ tenantId, tenantName, onClose, onSuccess }: {
  tenantId: string; tenantName: string; onClose: () => void; onSuccess: () => void;
}) {
  const [type, setType] = useState<'immediate' | 'graceful' | null>(null);
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const valid = type !== null && isValidReason(reason) && confirmName === tenantName;

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/api/internal/tenants/${encodeURIComponent(tenantId)}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ type, reason }),
      });
      onSuccess();
    } catch (err) {
      setError(errorMessage(err, 'Failed to suspend tenant.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Suspend ${tenantName}`} testId="suspend-modal" onClose={onClose}>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Suspension type</legend>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6, cursor: 'pointer' }}>
          <input type="radio" name="suspend-type" value="immediate" checked={type === 'immediate'} onChange={() => setType('immediate')} data-testid="suspend-type-immediate" />
          Immediate
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6, cursor: 'pointer' }}>
          <input type="radio" name="suspend-type" value="graceful" checked={type === 'graceful'} onChange={() => setType('graceful')} data-testid="suspend-type-graceful" />
          Graceful (24h notice email sent)
        </label>
      </fieldset>

      {type === 'graceful' && (
        <p data-testid="suspend-graceful-notice" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, padding: '8px 10px', background: 'rgba(245,158,11,0.10)', borderRadius: 4 }}>
          A 24-hour notice email will be sent to the tenant before suspension takes effect.
        </p>
      )}

      <label style={fieldLabelStyle} htmlFor="suspend-reason">Reason (minimum 10 characters)</label>
      <textarea
        id="suspend-reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        data-testid="suspend-reason"
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      <label style={fieldLabelStyle} htmlFor="suspend-confirm-name">
        Type <strong>{tenantName}</strong> to confirm
      </label>
      <input
        id="suspend-confirm-name"
        type="text"
        value={confirmName}
        onChange={e => setConfirmName(e.target.value)}
        data-testid="suspend-confirm-name"
        style={inputStyle}
      />

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      <div style={footerRowStyle}>
        <CancelButton onClick={onClose} />
        <SubmitButton label="Suspend tenant" busyLabel="Suspending…" busy={busy} disabled={!valid} onClick={() => void handleSubmit()} testId="suspend-submit" />
      </div>
    </ModalShell>
  );
}

// ── Reactivate ───────────────────────────────────────────────────────────

function ReactivateModal({ tenantId, tenantName, onClose, onSuccess }: {
  tenantId: string; tenantName: string; onClose: () => void; onSuccess: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const valid = isValidReason(reason);

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/api/internal/tenants/${encodeURIComponent(tenantId)}/reactivate`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      onSuccess();
    } catch (err) {
      setError(errorMessage(err, 'Failed to reactivate tenant.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Reactivate ${tenantName}`} testId="reactivate-modal" onClose={onClose}>
      <label style={fieldLabelStyle} htmlFor="reactivate-reason">Reason (minimum 10 characters)</label>
      <textarea
        id="reactivate-reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        data-testid="reactivate-reason"
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      <div style={footerRowStyle}>
        <CancelButton onClick={onClose} />
        <SubmitButton label="Reactivate tenant" busyLabel="Reactivating…" busy={busy} disabled={!valid} onClick={() => void handleSubmit()} testId="reactivate-submit" />
      </div>
    </ModalShell>
  );
}

// ── Tier change ──────────────────────────────────────────────────────────

function TierModal({ tenantId, tenantName, currentTier, onClose, onSuccess }: {
  tenantId: string; tenantName: string; currentTier: string; onClose: () => void; onSuccess: (newTier: string) => void;
}) {
  const [tier, setTier] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const valid = tier !== '' && tier !== currentTier && isValidReason(reason);

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/api/internal/tenants/${encodeURIComponent(tenantId)}/tier`, {
        method: 'PATCH',
        body: JSON.stringify({ tier, reason }),
      });
      onSuccess(tier);
    } catch (err) {
      setError(errorMessage(err, 'Failed to change tier.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Change tier — ${tenantName}`} testId="tier-modal" onClose={onClose}>
      <label style={fieldLabelStyle} htmlFor="tier-select">New tier</label>
      <select id="tier-select" value={tier} onChange={e => setTier(e.target.value)} data-testid="tier-select" style={inputStyle}>
        <option value="">Select a tier…</option>
        {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>

      <label style={fieldLabelStyle} htmlFor="tier-reason">Reason (minimum 10 characters)</label>
      <textarea
        id="tier-reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        data-testid="tier-reason"
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      <div style={footerRowStyle}>
        <CancelButton onClick={onClose} />
        <SubmitButton label="Change tier" busyLabel="Saving…" busy={busy} disabled={!valid} onClick={() => void handleSubmit()} testId="tier-submit" />
      </div>
    </ModalShell>
  );
}

// ── Seat limit ───────────────────────────────────────────────────────────

function SeatLimitModal({ tenantId, tenantName, currentSeatLimit, onClose, onSuccess }: {
  tenantId: string; tenantName: string; currentSeatLimit: number | null; onClose: () => void; onSuccess: () => void;
}) {
  // Pre-filled with the CURRENT LIMIT (the admin-set cap), not current
  // headcount — those are different fields (seat_limit vs seat_count; see
  // internal-tenants.router.ts's own file-header note on that distinction).
  // Empty when no limit has ever been set, per #2393 (no tier-default
  // fallback exists to pre-fill with instead).
  const [value, setValue] = useState(currentSeatLimit === null ? '' : String(currentSeatLimit));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0 && isValidReason(reason);

  async function handleSubmit() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/api/internal/tenants/${encodeURIComponent(tenantId)}/seat-limit`, {
        method: 'PATCH',
        body: JSON.stringify({ seat_limit: parsed, reason }),
      });
      onSuccess();
    } catch (err) {
      setError(errorMessage(err, 'Failed to update seat limit.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Set seat limit — ${tenantName}`} testId="seat-limit-modal" onClose={onClose}>
      <label style={fieldLabelStyle} htmlFor="seat-limit-value">Seat limit</label>
      <input
        id="seat-limit-value"
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={e => setValue(e.target.value)}
        data-testid="seat-limit-value"
        style={inputStyle}
      />

      <label style={fieldLabelStyle} htmlFor="seat-limit-reason">Reason (minimum 10 characters)</label>
      <textarea
        id="seat-limit-reason"
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={3}
        data-testid="seat-limit-reason"
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      <div style={footerRowStyle}>
        <CancelButton onClick={onClose} />
        <SubmitButton label="Set seat limit" busyLabel="Saving…" busy={busy} disabled={!valid} onClick={() => void handleSubmit()} testId="seat-limit-submit" />
      </div>
    </ModalShell>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

type ActiveModal = 'suspend' | 'reactivate' | 'tier' | 'seat-limit' | null;

interface Props {
  tenantId: string;
  tenantName: string;
  status: 'ACTIVE' | 'SUSPENDED';
  currentTier: string;
  currentSeatLimit: number | null;
  /** Re-fetches this row's own detail (members/flags/seat-limit) after any successful action. */
  onActionSuccess: (listPatch: { status?: 'Active' | 'Suspended'; tier?: string }) => void;
}

export default function TenantActionsFooter({ tenantId, tenantName, status, currentTier, currentSeatLimit, onActionSuccess }: Props) {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const closeModal = () => setActiveModal(null);

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {status === 'ACTIVE' ? (
        <button type="button" onClick={() => setActiveModal('suspend')} data-testid={`tenant-suspend-button-${tenantId}`} style={smallBtnStyle}>
          Suspend
        </button>
      ) : (
        <button type="button" onClick={() => setActiveModal('reactivate')} data-testid={`tenant-reactivate-button-${tenantId}`} style={smallBtnStyle}>
          Reactivate
        </button>
      )}
      <button type="button" onClick={() => setActiveModal('tier')} data-testid={`tenant-change-tier-button-${tenantId}`} style={smallBtnStyle}>
        Change tier
      </button>
      <button type="button" onClick={() => setActiveModal('seat-limit')} data-testid={`tenant-seat-limit-button-${tenantId}`} style={smallBtnStyle}>
        Set seat limit
      </button>

      {activeModal === 'suspend' && (
        <SuspendModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={closeModal}
          onSuccess={() => { closeModal(); onActionSuccess({ status: 'Suspended' }); }}
        />
      )}
      {activeModal === 'reactivate' && (
        <ReactivateModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={closeModal}
          onSuccess={() => { closeModal(); onActionSuccess({ status: 'Active' }); }}
        />
      )}
      {activeModal === 'tier' && (
        <TierModal
          tenantId={tenantId}
          tenantName={tenantName}
          currentTier={currentTier}
          onClose={closeModal}
          onSuccess={(newTier) => { closeModal(); onActionSuccess({ tier: newTier }); }}
        />
      )}
      {activeModal === 'seat-limit' && (
        <SeatLimitModal
          tenantId={tenantId}
          tenantName={tenantName}
          currentSeatLimit={currentSeatLimit}
          onClose={closeModal}
          onSuccess={() => { closeModal(); onActionSuccess({}); }}
        />
      )}
    </div>
  );
}
